/* global trapFocus, removeTrapFocus */

class DetailsModal extends HTMLElement {
  constructor() {
    super();
    this.closeAnimationFallbackDelay = 250;
    this.detailsEl = this.querySelector('details');
    this.toggle = this.querySelector('summary');
    this.modal = this.querySelector('.modal') || this.querySelector('.modal__content');
    this.closeButton = this.querySelector('.modal__close-button');
    this.closeOnScroll = this.classList.contains('header__search');
    this.detailsEl.addEventListener('click', this.handleClick.bind(this));
    this.closeButton?.addEventListener('click', (evt) => {
      evt.preventDefault();
      this.close();
    });
  }

  /**
   * Handles 'click' events on the modal.
   * @param {object} evt - Event object.
   */
  handleClick(evt) {
    if (!this.modal || !this.toggle) return;

    if (this.hasTransition === undefined) {
      this.hasTransition = window.getComputedStyle(this.modal).transitionDuration !== '0s';
    }

    const toggleClicked = this.toggle.contains(evt.target);

    if (!this.detailsEl.open) {
      if (toggleClicked) {
        evt.preventDefault();
        this.open();
      }
    } else if (evt.target === this.modal || toggleClicked) {
      evt.preventDefault();
      this.close();
    }
  }

  /**
   * Handles 'transitionend' events on the modal.
   * @param {object} evt - Event object.
   */
  handleTransitionEnd(evt) {
    if (evt.target === this.modal && this.detailsEl.classList.contains('is-closing')) {
      this.finishClose();

      // Remove event listener added on modal opening.
      this.detailsEl.removeEventListener('transitionend', this.transitionendHandler);
    }
  }

  /**
   * Schedules a fallback close completion in case transitionend does not fire.
   */
  setCloseFallback() {
    window.clearTimeout(this.closeFallbackTimeout);
    this.closeFallbackTimeout = window.setTimeout(() => {
      if (this.detailsEl.classList.contains('is-closing')) this.finishClose();
    }, this.closeAnimationFallbackDelay);
  }

  /**
   * Applies header state needed while the header search is open.
   */
  openHeaderSearch() {
    this.sectionHeader = this.sectionHeader || this.closest('.section-header');
    this.sectionHeader?.classList.add('menu-open');
    this.sectionHeader?.classList.remove('shopify-section-header-hidden');
  }

  /**
   * Clears header state applied while the header search was open.
   */
  closeHeaderSearch() {
    this.sectionHeader?.classList.remove('menu-open');
  }

  /**
   * Initiates opening of the modal.
   */
  open() {
    window.clearTimeout(this.closeFallbackTimeout);
    this.detailsEl.classList.remove('is-closing');
    document.body.classList.add('overflow-hidden');
    this.detailsEl.open = true;
    this.toggle?.setAttribute('aria-expanded', 'true');
    if (this.closeOnScroll) this.openHeaderSearch();

    trapFocus(this.detailsEl);

    // Add event handler (so the bound event listener can be removed).
    this.keyupHandler = (evt) => evt.key === 'Escape' && this.close();

    // Add event listener (for while modal is open).
    this.detailsEl.addEventListener('keyup', this.keyupHandler);
    if (this.closeOnScroll) {
      this.scrollHandler = this.scrollHandler || this.handleScrollClose.bind(this);
      window.addEventListener('wheel', this.scrollHandler, { passive: true, capture: true });
      window.addEventListener('touchmove', this.scrollHandler, { passive: true, capture: true });
    }

    if (this.hasTransition) {
      this.transitionendHandler = this.transitionendHandler || this.handleTransitionEnd.bind(this);
      this.detailsEl.addEventListener('transitionend', this.transitionendHandler);
    }
  }

  /**
   * Initiates closing of the modal.
   */
  close() {
    if (!this.detailsEl.open || this.detailsEl.classList.contains('is-closing')) return;

    if (this.closeOnScroll) {
      this.detailsEl.classList.add('is-closing');
      this.transitionendHandler = this.transitionendHandler || this.handleTransitionEnd.bind(this);
      this.detailsEl.addEventListener('transitionend', this.transitionendHandler);
      this.setCloseFallback();
    } else if (this.hasTransition) {
      this.detailsEl.classList.add('is-closing');
    } else {
      this.finishClose();
    }

    removeTrapFocus();

    // Remove event listener added on modal opening.
    this.detailsEl.removeEventListener('keyup', this.keyupHandler);
    if (this.closeOnScroll) {
      window.removeEventListener('wheel', this.scrollHandler, { capture: true });
      window.removeEventListener('touchmove', this.scrollHandler, { capture: true });
    }
  }

  /**
   * Closes header search for a scroll gesture and lets the page scroll immediately.
   */
  handleScrollClose() {
    this.close();
    document.body.classList.remove('overflow-hidden');
  }

  /**
   * Completes closing after any close animation has finished.
   */
  finishClose() {
    window.clearTimeout(this.closeFallbackTimeout);
    this.detailsEl.removeEventListener('transitionend', this.transitionendHandler);
    this.detailsEl.classList.remove('is-closing');
    this.detailsEl.open = false;
    this.toggle?.setAttribute('aria-expanded', 'false');
    document.body.classList.remove('overflow-hidden');
    if (this.closeOnScroll) this.closeHeaderSearch();
  }
}

customElements.define('details-modal', DetailsModal);
